import { each, camelCase, isEmpty, cloneDeep, isArray, at, forEach } from "lodash";
import "../assets/css/rcTree.css";
import i18n from "./i18n";
import { t } from "i18next";
import { customDecoding, customEncoding, deepSearchAndDecode, deepSearchAndEncode, genKey, stringifyJSONAttributes } from "./utilFunctions";
import { FIELD_TYPE } from "../constants/datatypes";
import { getUpdatedData } from "../components/RightContextMenu/ContextActions/NewRecordOutbound";


const inboundEncodedKeys = ["NOTES", "PATTERN", "VALUE",  
                           "BIT_MAP", 
                           "CODES", "COMMENT", "FILLCHAR", "START_VALUE", "SUB", 
                           "MASK", "FIELD_NAMES", "BODY", "EOD_TERMINATOR", "PARENT"];

const outboundEncodedKeys = ["NOTES",
                            "GLOBAL_DATA_TYPE_REPLACE", "PATTERN", "VALUE"
                            , "BIT_MAP", "CODES", "PARENT", 
                            "SOURCE_PARENT", "PARENT2", "FILLCHAR", "START_VALUE", "SUB", "MASK", 
                            "COMMENT", "COMMENTS", 
                            "HTTP_CUSTOM_HEADER_FIELDS", "CONNECTIONSTRING", "POST_FTP_SCRIPT", "PRE_FTP_SCRIPT", 
                            "SSH_SCRIPT", "APP_DEFINED_HEADER_FIELDS", "FILLCHAR"];

const workflowEncodedKeys = ["NOTES", "VALUE" 
                            , "BIT_MAP", "CODES", 
                            "PARENT", "ERROR_TYPE", "COMMENTS", "DESCRIPTION", "PATTERN" ,
                            "FILLCHAR", "START_VALUE", "SUB", "MASK", "FIELD_NAMES", "BODY", 
                            "APP_DEFINED_HEADER_FIELDS", "SYNC_GET_DATA_EOD_TERMINATOR", "EOD_TERMINATOR", 
                            "HTTP_CUSTOM_HEADER_FIELDS", "HTTP_QUERY_PARAMETERS", "FORM_DATA", "CONNECTIONSTRING", "POST_FTP_SCRIPT", "PRE_FTP_SCRIPT", 
                            "SSH_SCRIPT", "WS_OPERATION_NAME", "WS_OP_REQ_WF_GROUP", "WS_OP_REQ_WF_TEMP", 
                            "WS_OP_RES_WF_GROUP", "WS_OP_RES_WF_TEMP", "WS_OP_SOAP_ACTION"];

// These Keys will be encoded and decoded for selected workflow items;                            
const workflowEncodedKeysItemList = {
  "PATTERN" : ["TRANSLATE", "VARIABLE_ITEM"]
}

export function workflowGroupsToRcTree(data, type) {
  if (!isArray(data?.workflow_groups)) return []; 
  let wfGroups = [];
  let childrenKeyName = { inbound: "inbound_templates", outbound: "outbound_templates", workflow:  "workflows" }

  each(data.workflow_groups, (obj, key) => {
    let children = [];
    if (obj[childrenKeyName[type]]) {
      let workflowGroup = obj.name;
      let nodeType = type === "workflow" ? type : camelCase(childrenKeyName[type].slice(0, -1));
      each(obj[childrenKeyName[type]], (o, i) => {
        let invalidJSON = (o.ext === "json" && o.type === "doc")
        children.push({
          title: ["html", "mht", "pdf", "mhtml", "png", "jpeg", "jpg","gif"].includes(o.ext) || invalidJSON ? `${o.name}.${o.ext}` : o.name ? o.name : typeof o === "string" ? o : "Error reading Workflow Group Name", //fix because action=get_workflows structure changed in backend?
          key: `wfg-${key}-${i}`,
          type: ["html", "mht", "pdf", "mhtml", "png", "jpeg", "jpg", "gif"].includes(o.ext) || invalidJSON ? "DOCUMENT" : nodeType,
          isLeaf: true,
          datatype: o.datatype,
          workflowGroup,
          location: o.location,
          ext: o.ext,
          name: o.name,
          ...(o.locked_by && { lockedBy: o.locked_by }),
        });
      });
    }
    wfGroups.push({
      title: obj.name,
      children: children,
      key: `wfg-${key}`,
      type: "workflowGroup",
      location: obj.wfg_location
    });
  });
  return wfGroups;
}

export const getNewDataWithLoadingStates = (data, templateName, workflowName, isLoading) => {
  let decodedWorkflowName;
  let decodedTemplateName;
  
  try {
    decodedWorkflowName = decodeURIComponent(workflowName);
  } catch (error) {
    decodedWorkflowName = workflowName;
  }
  
  try {
    decodedTemplateName = decodeURIComponent(templateName);
  } catch (error) {
    decodedTemplateName = templateName;
  }
  return data?.map(e => {
    if(decodedWorkflowName === e?.title) {
      const newTemplatesData = e?.children?.map(o => {
        if (o.title === decodedTemplateName) {
          o["disabled"] = isLoading
        }
        return o;
      })
      e["children"] = newTemplatesData;
    }
    return e;
  })
}

// convert inbound response data into format usable by rc-tree
export function inboundTemplateToRcTree(data, treeType, datatype) {
  const templateData = data?.TEMPLATE[treeType];
  let children = [];
  let flatIndex = 0;
 
  if (isEmpty(templateData)) {
    return treeType === "BODY" ? [
      {
        key: "ib-0",
        nodeId: genKey('root'),
        alternateNodeId: genKey('root'),
        title: i18n.t("BODY"),
        ICON_DISPLAY: "TEMPLATE_BODY_DOTS",
        TYPE: "root",
        children: [],
        treeType: "BODY",
        flatIndex
      },
    ] : [];
  }

  // convert RECORD DETAIL item to proper format
  function handleRecordDetails(detail, i, parId) {
    let detailtype = Object.keys(detail)[0];
    let data = detail[detailtype];
    let id = `${parId}-${i}`;
    // The node's display text can be different. 
    // There does not seem to be a consistent prop that can be used for node title.
    let title = data.DESCRIPTION || data.ATTRIBUTE_NAME ||  data.ELEMENT_NAME || data.MAPNAME || data.TYPE || "";
    if (data.ICON_DISPLAY === "ELEMENT" || data.ICON_DISPLAY === 'REPEATING_ELEMENT') {
      title = data.MAPNAME || data.DESCRIPTION || data.ATTRIBUTE_NAME ||  data.ELEMENT_NAME || data.TYPE || "";
      title = `<${title}>`;
    } // add case for ELEMENTS that have no children
    if (data.ICON_DISPLAY === 'FIELD_GROUP' || data.ICON_DISPLAY === 'REPEATING_FIELD_GROUP') title = data.TYPE || data.DESCRIPTION || data.MAPNAME || ""; // add case for FIELD_GROUP
    if (data.ICON_DISPLAY === 'ATTRIBUTE') title = data.ATTRIBUTE_NAME || data.DESCRIPTION || data.MAPNAME || "";

    let obj = {
      ...data,
      key: id,
      nodeId: genKey(title),
      alternateNodeId: genKey(title),
      title,
      type: data.ICON_DISPLAY,
      //isLeaf: detail.FIELD_GROUP ? false : true, // Setting isLeaf to false for the field group to act as a container for its children
      fieldNumber: datatype === "EDI" ? `${i}` : i + 1,
      // DESCRIPTION: title,
      flatIndex,
    };
    
    // Assign field number to field groups for EDI and HL7 datatypes
    if ((["EDI", "HL7"].includes(datatype) && (data.ICON_DISPLAY?.includes('FIELD_GROUP') || data.ICON_DISPLAY === 'REPEATING_FIELD_GROUP'))) {
      obj.fieldNumber = datatype === "EDI" ? `${i}` : i + 1;
    }
    
    if (data.ICON_DISPLAY === 'ELEMENT' || data.ICON_DISPLAY === 'REPEATING_ELEMENT') obj['children'] = [];

    if (detail.FIELD_GROUP) {
      obj.children = [];
      if (!data.DESCRIPTION) delete obj.DESCRIPTION;
      each(detail.FIELD_GROUP.RECORD_DETAILS, o => {
        if ((datatype === "JSON" || datatype === "XML") && o?.hasOwnProperty('GUI_XML_SORT_ORDER')) {
          obj?.children?.splice(
            o?.GUI_XML_SORT_ORDER,
            0,
            handleRecordDetails(o, obj.children.length || 0, id)
          );
        } else {
          obj.children.push(
            handleRecordDetails(o, obj.children.length || 0, id)
          );
        }

      });
      delete obj.FIELD_GROUP;
    } 

    //if node is field, display at beginning of title. 
    //if fieldtype is constant and there is no column name, also display (CONSTANT) after Field:. 
    // -this can be tested with Database datatype
    if (detailtype === "FIELD" || obj.ICON_DISPLAY === 'FIELD') {
      if(obj.ICON_DISPLAY === 'ATTRIBUTE' || obj.ICON_DISPLAY === 'ELEMENT' || obj.ICON_DISPLAY === 'REPEATING_ELEMENT'){ 
        // obj.title = obj.title 
      } else {
        obj.title = `${t('LBL_FIELD')} ${(obj.FIELDTYPE==='CONSTANT' && !obj.COLUMN_NAME) ? `(${t('CONSTANT')})` : ''} ${obj.title}`;
      }
    }
    
    //if node is comment, display at beginning of title
    if (obj.COMMENT || obj.ICON_DISPLAY === 'COMMENT') {
      obj.title = `${t('LBL_COMMENT')} ${customDecoding(data?.COMMENT) || ''}`;
    }
    //return info obj in rc-tree format
    if(obj?.children?.length) obj.isLeaf = false
    else obj.isLeaf = true
    flatIndex++
    return obj;
  }

  // convert RECORD item into proper format
  function handleRecord(record, i, parId) {
    let recordChildren = [];
    let id = `${parId}-${i}`;
    // let title = record.ICON_DISPLAY === "ELEMENT" || record.ICON_DISPLAY === 'REPEATING_ELEMENT' ? `<${record.TYPE}>` : record.TYPE;
    let title =
      record.ICON_DISPLAY === "ELEMENT" ||
      record.ICON_DISPLAY === "REPEATING_ELEMENT"
        ? record.TYPE
          ? `<${record.TYPE}>`
          : `<${record.ELEMENT_NAME}>`
        : record.TYPE;

    let obj = { 
      ...record, 
      key: id,
      nodeId: genKey(title),
      alternateNodeId: genKey(title),
      title, 
      children: recordChildren,
      type:  record.ICON_DISPLAY,
      flatIndex,
    };
    flatIndex++

    //dont need these props in our tree. tree will use `children` prop to nest both.
    delete obj.RECORD;
    delete obj.RECORD_DETAILS;
    if (record.RECORD_DETAILS) {
      each(record.RECORD_DETAILS, o => {
        if ((datatype === "JSON" || datatype === "XML") && o?.hasOwnProperty('GUI_XML_SORT_ORDER')) {
          recordChildren.splice(o.GUI_XML_SORT_ORDER, 0, handleRecordDetails(o, recordChildren.length, id));
        } else {
          recordChildren.push(handleRecordDetails(o, recordChildren.length, id));
        }
      });
    }

    if (record.RECORD) {
      each(record.RECORD, o => {
        if ((datatype === "JSON" || datatype === "XML") && o?.hasOwnProperty('GUI_XML_SORT_ORDER')) {
          recordChildren.splice(o.GUI_XML_SORT_ORDER, 0, handleRecord(o, recordChildren.length, id));
        } else {
          recordChildren.push(handleRecord(o, recordChildren.length, id));
        }
      });
    }
    return obj;
  }

  let root = {};

  //check for header, body, trailer
  if (treeType === "BODY") {
    root = {
      key: `ib-0`,
      title: i18n.t("BODY"),
      nodeId: genKey("root"),
      alternateNodeId: genKey("root"),
      ICON_DISPLAY: "TEMPLATE_BODY_DOTS",
      children,
      TYPE: "root",
      treeType,
      state: { opened: true },
      flatIndex: 0
    };

    flatIndex = 1
    // loop through RECORDS
    if (templateData.RECORD) {
      each(templateData.RECORD, (record, i) => {
        children.push(handleRecord(record, children.length, "ib-0"));
      });
    }
  } else {
    let record = templateData.RECORD[0];
    let title = record.TYPE;
    root = {
      ...record,
      key: treeType === "TRAILER" ? "ibt-0" : "ibh-0",
      nodeId: genKey(title),
      alternateNodeId: genKey(title),
      title,
      ICON_DISPLAY: record.ICON_DISPLAY,
      children: [],
      treeType,
      flatIndex: 0
    };

    const firstKey = {
      HEADER: "ibh-0",
      BODY: "ib-0",
      TRAILER: "ibt-0"
    }

    // flatIndex = 1
    if (record.RECORD_DETAILS) {
      each(record.RECORD_DETAILS, (obj, i) => {
        root.children.push(
          handleRecordDetails(obj, root.children.length, firstKey)
          );
        });
    }
    if (record) {
      each(record.RECORD, (record, i) => {
        root.children.push(handleRecord(record, root.children.length, firstKey));
      });
    }
  }

  return reindexTreeNodes([root], datatype, true);
}

// convert outbound response data into format usable by rc-tree
export function outboundTemplateToRcTree(data, templateHeader, tab) {
  const datatype = templateHeader.FILETYPE;

  // Special case: GraphQL templates do not use the rc-tree structure on the right-hand side.
  // Return an empty translated tree so UI will not attempt to render RECORD nodes.
  if (datatype === "GraphQL") return [];

  let flatIndex = 0;
  const titleMapping = {
    INSERT: 'INTO',
    UPDATE: '',
    DELETE: ''
  }

  if (data && data?.RECORD?.length === 0) {
    const iconMapping = {
      CSV: "RECORD",
      JSON: "RECORD_TOP",
    };
    return  [
      {
        key: tab === "BODY" ? "ob-0" : tab === "FILE_HEADER" ? "obh-0" : "obt-0",
        nodeId: genKey('root'),
        alternateNodeId: genKey('root'),
        title: i18n.t("LBL_RECORD"),
        LOOP: 'SINGLE',
        ICON_DISPLAY: iconMapping[datatype],
        children: [],
        flatIndex
      },
    ] 
  }

  function routeChild(child, index, pIndex, parId, par, ifId) {
    let processedChild;

    //IF/ELSE special cases
    if (["IF", "ELSE"].includes(index)) {
      if (index === "IF") {
        index = `${pIndex}`;
        child = { "IF": {...child} }

      }
      else {
        index = `${pIndex}`;
        child = { "ELSE": {...child} }
      }
    }
    
    let nodeData = child[Object.keys(child)[0]]
    let title = nodeData.ATTRIBUTE_NAME || nodeData.ELEMENT_NAME || nodeData.DESCRIPTION || nodeData.NAME  || 
                nodeData.CLASS || nodeData.TYPE || nodeData.REPORT_NAME || nodeData.COMMENT || "UNNAMED";
    
    if (nodeData.ICON_DISPLAY === 'QUERY') {
      if (datatype !== 'DATABASE') {
          title = nodeData?.NAME || nodeData?.MAPNAME || nodeData?.DESCRIPTION;
      } else if (nodeData?.TYPE !== 'QUERY' && nodeData.TABLE_NAME && nodeData?.SCHEMA_NAME && nodeData.SCHEMA_NAME !== '-- Select --') {
          title = `${nodeData?.STATEMENT} ${titleMapping[nodeData?.STATEMENT]} ${nodeData?.TABLE_NAME}`;
      } else {
          title = nodeData?.NAME || nodeData?.MAPNAME || nodeData?.DESCRIPTION;
      }
    }
              
    processedChild = {
      ...nodeData,
      nodeId: ifId || genKey(title),
      alternateNodeId: genKey(title),
      key: `${parId}-${pIndex}` || {header: "obh-0", body: "ob-0", trailer: "obt-0"}[tab],
      //title : nodeData.ICON_DISPLAY==="RECORD"?`${t("LBL_RECORD")} ${title}`:title,
      title: ["RECORD", "RECORD_LOOP", "QUERY"].includes(nodeData.ICON_DISPLAY) && nodeData.TYPE !== 'QUERY' ? `${t("LBL_RECORD")} ${title || ""}` 
        : ["VARIABLE"].includes(nodeData.ICON_DISPLAY) ? `${t("VARIABLE_")} ${title || ""}` 
        : ["CONDITION_IF"].includes(nodeData.ICON_DISPLAY) ? `${t("CONDITIONAL_FIELDS_")} ${title || ""}` 
        : ["VARIABLE_COLLECTION"].includes(nodeData.ICON_DISPLAY) ? `${t("VARIABLE_COLL")} ${nodeData?.NAME || ""}` 
        : ["SEQUENCE"].includes(nodeData.ICON_DISPLAY) ? `${t("SEQUENCE_")} ${nodeData?.NAME || "UNNAMED"}` 
        : ["QUERY"].includes(nodeData.ICON_DISPLAY) && nodeData.TYPE === 'QUERY' ? `${t("CUST_DB_FIELD_")} ${nodeData.NAME || nodeData?.MAPNAME || "UNNAMED"}` 
        : ["PLUGIN"].includes(nodeData.ICON_DISPLAY) ? `${t("PLUGIN_")}${(nodeData.CLASS !== "" && nodeData.CLASS !== undefined) ? ` ${nodeData.CLASS}` : ""}:${(nodeData.NAME !== "" && nodeData.NAME !== undefined) ? ` ${nodeData.NAME}` : ""}`
        : ["ACTION_ITEM"].includes(nodeData.ICON_DISPLAY) ? `${t("_ACTION_")} ${nodeData?.NAME || title || ""}` 
        : ["TRACKING_REPORT_ITEM"].includes(nodeData.ICON_DISPLAY) ? `${t("LBL_TRACKING_REPORT")} ${title || ""}` 
        : ["FIELD_GROUP","REPEATING_FIELD_GROUP"].includes(nodeData.ICON_DISPLAY) ? `${t('FIELD_GROUP')}: ${title || ""}`
        : ["COMMENT"].includes(nodeData.ICON_DISPLAY) ? `${t('LBL_COMMENT')} ${customDecoding(nodeData?.COMMENT) || ""}`
        : nodeData.ELEMENT_NAME && !nodeData.ATTRIBUTE_NAME ? tab === "BODY" ? `<${title || ""}>`: `${t('LBL_RECORD')} <${title}>`
        : Object.keys(child)[0] === "FIELD" && ((datatype !=='XML') || (tab !== "BODY" && datatype === "XML")) ? nodeData.FIELDTYPE === 'CONSTANT' ? `${t("LBL_FIELD")} (${'CONSTANT'}) ${title || ""}` : `${t("LBL_FIELD")} ${title || ""}` 
        : nodeData.ICON_DISPLAY === "ELEMENT" && nodeData.XML_RECORD === "TRUE" ? tab === "BODY" ? `${t('LBL_RECORD')} ${title}` : `${t('LBL_RECORD')} ${title}`
        : (nodeData.ICON_DISPLAY === "ELEMENT" || nodeData.ICON_DISPLAY === "REPEATING_ELEMENT") && tab !== "BODY" ? `${t('LBL_RECORD')} ${title}`
        : title || "",
      children: [],
      // DESCRIPTION: nodeData?.FIELDTYPE === 'CONSTANT' ? 'UNNAMED' : nodeData?.DESCRIPTION || '',
    }
    
    // For elements with LOOP="MULTI" and SOURCE_TYPE="INBOUND_DATA", ensure loop properties are at element level
    if (processedChild.LOOP === "MULTI" && processedChild.SOURCE_TYPE === "INBOUND_DATA" && processedChild.FIELD) {
      // Extract TYPE from FIELD.RECORD_TYPE if not present at element level
      if (!processedChild.TYPE && processedChild.FIELD.RECORD_TYPE) {
        processedChild.TYPE = processedChild.FIELD.RECORD_TYPE;
      }
      // Extract LOOP_SOURCE_NAME from FIELD.MAPNAME if not present at element level  
      if (!processedChild.LOOP_SOURCE_NAME && processedChild.FIELD.MAPNAME) {
        processedChild.LOOP_SOURCE_NAME = processedChild.FIELD.MAPNAME;
      }
      // Extract PARENT from FIELD.PARENT if not present at element level
      if (!processedChild.PARENT && processedChild.FIELD.PARENT) {
        processedChild.PARENT = processedChild.FIELD.PARENT;
      }
    }

    //Fields don't seem to have ICON_DISPLAY prop...
    //have to find out which icon to use
    if (!processedChild.ICON_DISPLAY && Object.keys(child)[0] === "FIELD") {
      if (processedChild.ELEMENT_NAME && !processedChild.XML_ATTRIBUTE) processedChild.ICON_DISPLAY = "ELEMENT";
      else if (processedChild.XML_ATTRIBUTE) processedChild.ICON_DISPLAY = "ATTRIBUTE";
      else processedChild.ICON_DISPLAY = "FIELD";
    }

    if (processedChild.ICON_DISPLAY === "ELEMENT" && processedChild.XML_RECORD === "TRUE") processedChild.ICON_DISPLAY = "RECORD_LOOP"

    if (Object.keys(child)[0] === "FIELD") {
      if (title === "UNNAMED" && nodeData.MAPNAME && !nodeData.DESCRIPTION) {
        processedChild.title = `${t("LBL_FIELD")} ${nodeData.MAPNAME}`;
      }
      
      //filter parent's children arr to only fields, then determine the fieldnumber 
      //based on new relative index in the filtered array. Will default to i + 1;
      if (["FIELD", "DATABASE_FIELD"].includes(processedChild.ICON_DISPLAY)) {
        let fieldN = false;
        [...par.children, processedChild]
          .filter((o) => ["FIELD", "DATABASE_FIELD"].includes(o.ICON_DISPLAY))
          .forEach((field, i) => {
            if (field.nodeId === processedChild.nodeId) fieldN = datatype === "EDI" ? i : i + 1;
          });
        processedChild.fieldNumber = fieldN === false ? index + 1 : fieldN;
      }
      
      // Assign field number to field groups for EDI and HL7 datatypes
      if ((["EDI", "HL7"].includes(datatype) && (processedChild.ICON_DISPLAY?.includes('FIELD_GROUP') || processedChild.ICON_DISPLAY === 'REPEATING_FIELD_GROUP'))) {
        let fieldN = false;
        [...par.children, processedChild]
          .filter((o) => (["EDI", "HL7"].includes(datatype) && (o.ICON_DISPLAY?.includes('FIELD_GROUP') || o.ICON_DISPLAY === 'REPEATING_FIELD_GROUP')) || ["FIELD", "DATABASE_FIELD"].includes(o.ICON_DISPLAY))
          .forEach((field, i) => {
            if (field.nodeId === processedChild.nodeId) fieldN = datatype === "EDI" ? i : i + 1;
          });
        processedChild.fieldNumber = fieldN === false ? index + 1 : fieldN;
      }
    }

    function loopChildren(parentKey) {
      each(processedChild[parentKey], (child, i) => {
        if (Object.keys(child).length === 1) {
          // root Record's normal children get routed for processing
          processedChild.children.push(routeChild(child, i, processedChild.children.length, processedChild.key, processedChild));
        } else {
          // CASE: node has multiple keys -
          // Not optimal... but the templates sometimes come with nested nodes that have to be
          // displayed as siblings. So far only seen with IF/ELSE, but making it dynamic just incase.
          each(child, (c, ind) => {
            //TODO: THIS NEEDS WORK
            processedChild.children.push(routeChild(c, ind, processedChild?.children?.length, processedChild.key, processedChild));
          })
        }
      });
      delete processedChild[parentKey];
    }
 
    //check node for children
    if (processedChild.ATTRIBUTE_LIST) loopChildren("ATTRIBUTE_LIST");
    if (processedChild.RECORD_DETAILS) loopChildren("RECORD_DETAILS");
    if (processedChild.IF_FIELDLIST) loopChildren("IF_FIELDLIST");
    if (processedChild.ELSE_FIELDLIST) loopChildren("ELSE_FIELDLIST");
      
    return processedChild;
  }
  let title = data.DESCRIPTION || data.ELEMENT_NAME || data.TYPE || data?.FIELD?.DESCRIPTION || templateHeader.XML_BODY_NAME || "";
  if (datatype === "XML" && (data.ICON_DISPLAY === "ELEMENT" || data.ICON_DISPLAY === 'REPEATING_ELEMENT')) {
    if (tab === "BODY") {
      const xmlRootName = data.ELEMENT_NAME || data.DESCRIPTION || data.MAPNAME || data.TYPE || templateHeader?.XML_BODY_NAME || "";
      title = `<${xmlRootName}>`;
    } else {
      //xml header/trailer uses csv node types
      title = `${t("LBL_RECORD")} ${title}`;
    }
  }
  if (datatype === 'DATABASE' && data.ICON_DISPLAY === 'QUERY' && data?.TYPE !=='QUERY' && data.TABLE_NAME && data?.SCHEMA_NAME) title = `${data?.STATEMENT} ${titleMapping[data?.STATEMENT]} ${data?.TABLE_NAME}`

  let root = {
    ...data,
    key: tab === "BODY" ? "ob-0" : tab === "FILE_HEADER" ? "obh-0" : "obt-0",
    title: ["RECORD", "RECORD_LOOP", "JSON_TOP_RECORD", "QUERY", "RECORD_TOP"].includes(data.ICON_DISPLAY) && data.TYPE !== 'QUERY' ?`${t("LBL_RECORD")} ${title ||""}`: title ||"",
    children: [],
    nodeId: genKey('root'),
    alternateNodeId: genKey('root'),
    flatIndex,
  }
  if (datatype === "XML") root.xmlroot = true;
  
  const listToCheck = ["ATTRIBUTE_LIST", "RECORD_DETAILS"];

  listToCheck.forEach((prop) => {
    if (root[prop]) {
      each(root[prop], (child, i) => {
        if (Object.keys(child).length === 1) {
          // root Record's normal children get routed for processing
          root.children.push(
            (routeChild(child, i, root.children.length, root.key, root))
          );
        } else {
          // CASE: node has multiple keys -
          // Not optimal... but the templates sometimes come with nested nodes that have to be
          // displayed as siblings. So far only seen with IF/ELSE, but making it dynamic just incase.
          let ifId = genKey("");
          each(child, (c, ind) => {
            root.children.push(
              routeChild(c, ind, root?.children?.length, root.key, root, ifId)
            );
          });
        }
      });
      delete root[prop];
    }
  });

  if (tab !== "BODY" && (data?.ICON_DISPLAY === "REPEATING_ELEMENT" || data?.ICON_DISPLAY === "ELEMENT") && ! data?.DESCRIPTION ) {
      //in xml outbound, template has a ghost root node in header/trailer, which needs to be intangible for users.
      return []
  } else {
    return getUpdatedData([(root)], datatype, false, false, {"BODY" : "body", "FILE_HEADER" : "header", "FILE_TRAILER" : "trailer"}[tab]);
  }
}

/** Fix stale VARIABLE session WF_ITEM_ID when WFITEM/WF_ITEM_LOCATION matches a unique workflow item NAME. */
function normalizeWorkflowVariableWfItemIdsDeep(data) {
  if (!Array.isArray(data)) return data;
  const sessionTypes = new Set(["SESSION_DATA", "CALL_WORKFLOW_DATA"]);
  const itemIcons = new Set([
    "VARIABLE_ITEM", "GET_DATA", "TRANSLATE", "SEND_DATA", "LOOP_ITEM", "CONDITION_IF",
    "CALL_PLUGIN", "CALL_WORKFLOW", "TRADING_PARTNER_LOOKUP", "ACTION", "ERROR_HANDLER", "TRACKING_REPORT_ITEM",
  ]);
  const validIds = new Set();
  const nameToIds = new Map();
  const collect = (nodes) => {
    nodes.forEach((node) => {
      if (node?.NAME && node?.ID != null && node.ID !== "" && itemIcons.has(node?.ICON_DISPLAY)) {
        const idStr = String(node.ID);
        validIds.add(idStr);
        if (!nameToIds.has(node.NAME)) nameToIds.set(node.NAME, []);
        nameToIds.get(node.NAME).push(idStr);
      }
      if (node?.children?.length) collect(node.children);
    });
  };
  collect(data);
  const repair = (nodes) => {
    nodes.forEach((node) => {
      if (node?.ICON_DISPLAY === "VARIABLE_ITEM" && Array.isArray(node.VARIABLE)) {
        node.VARIABLE.forEach((action) => {
          if (!action || typeof action !== "object" || !sessionTypes.has(action.RECORD_TYPE)) return;
          const hint = action.WFITEM || action.WF_ITEM_LOCATION;
          if (!hint || typeof hint !== "string") return;
          const cur = action.WF_ITEM_ID != null && action.WF_ITEM_ID !== "" ? String(action.WF_ITEM_ID) : "";
          if (cur && validIds.has(cur)) return;
          const c = nameToIds.get(hint);
          if (c?.length === 1) action.WF_ITEM_ID = c[0];
        });
      }
      if (node?.children?.length) repair(node.children);
    });
  };
  repair(data);
  return data;
}

export function workflowTemplateToRcTree(data) {
  let wfRoot = data?.WORKFLOW_DEFINITION?.WORKFLOW || {};

  //ACTIONS:
  //Get Data                GET_DATA
  //Translate Data          TRANSLATE
  //Send Data               SEND_DATA
  //Variable                VARIABLE_ITEM
  //Loop                    LOOP_ITEM
  //IF                      ITEM_LIST
  //ELSE                    ELSE_WF_ITEM inside IF node
  //Plugin                  CALL_PLUGIN
  //Call Workflow           CALL_WORKFLOW
  //Trading Partner Lookup  TRADING_PARTNER_LOOKUP
  //Action                  ACTION
  //Comment                 COMMENT
  //Error Handler           ERROR_HANDLER
  //Tracking Report         TRACKING_REPORT_ITEM

  //accepts an array of children nodes and a parent key, returns an array of processed children nodes
  function routeChildren(childArr, parentKey) {
    let newChildren = [];
    let index = 0;
    forEach(childArr, (item, _) => {
      if (item.ELSE_WF_ITEM) {
        let sharedID = genKey(`${item.NAME}${item.ID}${item.STRUCTURE_ID}`);
        //for IF/ELSE node, split node into 2 for rcTree and give them a shared ID to link them
        newChildren.push(processNode(item, index++, parentKey, sharedID));
        newChildren.push(processNode(item, index++, parentKey, sharedID, true));
      } else {
        newChildren.push(processNode(item, index++, parentKey));
      }
    });
    return newChildren;
  }

  //process a node and give it rcTree props based on different node type requirements
  function processNode(node, index, parentKey, forcedID, isElse) {
    let nodeData;
    
    if(node?.hasOwnProperty("PLUGIN")){
      const { NAME, ID, ACTION, CONCURRENT, COMMENTS, ENABLED, LOG_WFITEM_DETAILS, ICON_DISPLAY, PLUGIN } = node;
      nodeData = { 
        ...(ID ? { ID } : {}),
        ACTION,
        CONCURRENT,
        ...(COMMENTS ? { COMMENTS } : {}),
        ENABLED,
        LOG_WFITEM_DETAILS,
        ICON_DISPLAY,
        ...(PLUGIN || {}),
        NAME,
      };

    }
    else
      nodeData = node;

    let processedNode = {
      ...nodeData,
      key: `${parentKey}-${index}`,
      nodeId: forcedID || genKey(nodeData.NAME || nodeData.ACTION || ""),
      alternateNodeId: genKey(nodeData.NAME || nodeData.ACTION || ""),
      children: [],
    };

    //check for IF/ELSE node
    if (nodeData.ACTION === "ITEM_LIST") {
      //check if this is the ELSE node
      if (isElse) {
        if(nodeData?.EPOSITIONX !== undefined && nodeData?.EPOSITIONY !== undefined){
          processedNode.POSITIONX = nodeData?.EPOSITIONX;
          processedNode.POSITIONY = nodeData?.EPOSITIONY;
        }
        processedNode.title = nodeData.ELSE_NAME;
        processedNode.ICON_DISPLAY = "CONDITION_ELSE";
        if (nodeData.ELSE_WF_ITEM?.length > 0) {
          processedNode.children = routeChildren(nodeData.ELSE_WF_ITEM, processedNode.key);
          delete processedNode.ELSE_WF_ITEM; // dont want the ELSE_WF_ITEM in the rcTree data.
        }
      } else {
        processedNode.title = nodeData.NAME;
        processedNode.ICON_DISPLAY = "CONDITION_IF";
        if (nodeData.WF_ITEM?.length > 0) {
          processedNode.children = routeChildren(nodeData.WF_ITEM, processedNode.key);
          if (processedNode.ELSE_WF_ITEM) delete processedNode.ELSE_WF_ITEM; // dont want the ELSE_WF_ITEM in the rcTree data.
          if (processedNode.WF_ITEM) delete processedNode.WF_ITEM; // dont want the WF_ITEM in the rcTree data.
        }
      }
    } else {
      //for all other node types
      processedNode.title = nodeData.NAME || t("UNNAMED");
      processedNode.ICON_DISPLAY = nodeData.ACTION;
      if (nodeData.WF_ITEM?.length > 0) {
        processedNode.children = routeChildren(nodeData.WF_ITEM, processedNode.key);
        
      }

      if (nodeData.ACTION_TYPE === "Stop All Workflow Processing") {
        processedNode.ICON_DISPLAY = "STOP_ACTION";
      }
    }

    // if (processedNode.WF_ITEM) delete processedNode.WF_ITEM; // dont want the WF_ITEM in the rcTree data.
    return processedNode;
  }

  let root = {
    ICON_DISPLAY: "OBJECTS",
    children: [],
    // flatIndex: 0, //not sure yet if we need a flat index for workflow
    key: `wf-0`,
    nodeId: genKey("root"),
    alternateNodeId: genKey("root"),
    state: { opened: true },
    title: wfRoot?.NAME || "UNNAMED", //added as potential fallback, will be replaced later.
    ...wfRoot
  };
  // delete root.WF_ITEM; // dont want the WF_ITEM in the rcTree data.

  if (wfRoot?.WF_ITEM?.length > 0) {
    root.children = routeChildren(wfRoot.WF_ITEM, "wf-0");
  }

  return [root];
}

export function reindexTreeNodes(tdata, datatype, initialSort) {
  if (!tdata.length) return tdata;

  //copy translatedData
  let data = cloneDeep(tdata);

  //loop parent node, set key and fieldNumber, then search and loop children of parent node.
  function loopChildren(parent) {
    let fieldNumber = -1;
    if (initialSort) parent?.children?.sort((a, b) => Number(a?.GUI_XML_SORT_ORDER) - Number(b?.GUI_XML_SORT_ORDER)); // Nodes should be in order according to GUI_XML_SORT_ORDER initially
    parent.children.forEach((o, k) => {
      if (["JSON", "XML"].includes(datatype) && o.hasOwnProperty("GUI_XML_SORT_ORDER")) o.GUI_XML_SORT_ORDER = `${k + 1}` // Every time data changes then order will also change
      // Field Number should be for Field and Field Group only it should not include other nodes (like comment)
      if ((["EDI", "HL7"].includes(datatype) && (o?.ICON_DISPLAY?.includes('FIELD_GROUP') || o?.ICON_DISPLAY === 'REPEATING_FIELD_GROUP')) || FIELD_TYPE[o?.ICON_DISPLAY]) fieldNumber++;
      o.key = `${parent.key}-${k}`
      //EDI datatype fieldNumber starts at 0. Other datatypes, start at 1.
      const tempFieldNumber = (datatype === "EDI" && (o?.ICON_DISPLAY?.includes('FIELD_GROUP') || o?.ICON_DISPLAY === 'REPEATING_FIELD_GROUP')) || FIELD_TYPE?.[o?.ICON_DISPLAY] ? fieldNumber : undefined;
      if (o.fieldNumber) o.fieldNumber = datatype === 'EDI' ? tempFieldNumber : tempFieldNumber + 1;
      if(datatype === 'EDI' && parent.ICON_DISPLAY === 'FIELD_GROUP') o.fieldNumber = tempFieldNumber +1
      
      // Assign field number to field groups for EDI and HL7 datatypes
      if ((["EDI", "HL7"].includes(datatype) && (o?.ICON_DISPLAY?.includes('FIELD_GROUP') || o?.ICON_DISPLAY === 'REPEATING_FIELD_GROUP'))) {
        o.fieldNumber = datatype === 'EDI' ? fieldNumber : fieldNumber + 1;
      } 
      if (o?.ICON_DISPLAY?.includes("ELEMENT")) {
        if (o?.children?.length) {
          try{
            o.TYPE = o?.TYPE || o?.ELEMENT_NAME || o?.MAPNAME || 
            ["ELEMENT_NAME", "MAPNAME"].forEach(e => {
              if (o?.hasOwnProperty(e)) delete o?.[e];
            })
          }
          catch(err) {
            console.error(o);
          }
        }else{
          o.ELEMENT_NAME = o?.ELEMENT_NAME || o?.MAPNAME || o?.TYPE
          o.MAPNAME = o?.MAPNAME || o?.ELEMENT_NAME || o?.TYPE
         if (o?.hasOwnProperty("TYPE")) delete o?.["TYPE"]
        }
      }
      if (o?.children?.length) loopChildren(o);
    });
  }

  //loop root array
  data.forEach(o => {
    if (o.children?.length) loopChildren(o);
  })

  //return altered translatedData
  return data;
}

export function setTemplateData(state, payload , isValidTemplate) {
  let screen = payload.screen;

  if (payload?.TEMPLATE && screen !== 'workflow') {
    let tp = payload.TEMPLATE;
    let rt = state[screen].rightTree.tree;
    let typeHeader = {
      inbound: "HEADER",
      outbound: "FILE_HEADER"
      //workflow
    };
    let typeTrailer = {
      inbound: "TRAILER",
      outbound: "FILE_TRAILER",
      //workflow
    };

    rt.condition = deepSearchAndDecode(tp.CONDITION || [], screen === "outbound" ? outboundEncodedKeys : inboundEncodedKeys); 
    rt.templateHeader = deepSearchAndDecode(tp.TEMPLATE_HEADER || [], screen === "outbound" ? outboundEncodedKeys : inboundEncodedKeys);
    rt.initialTemplateHeader = deepSearchAndDecode(tp.TEMPLATE_HEADER || [], screen === "outbound" ? outboundEncodedKeys : inboundEncodedKeys);
    rt.tradingPartner = deepSearchAndDecode(tp.TRADING_PARTNER || [], screen === "outbound" ? outboundEncodedKeys : inboundEncodedKeys);
    rt.initialTradingPartner = deepSearchAndDecode(tp.TRADING_PARTNER || [], screen === "outbound" ? outboundEncodedKeys : inboundEncodedKeys);
    
    function getTranslation(tab) {
      if (screen === 'inbound') return deepSearchAndDecode(inboundTemplateToRcTree(payload, tab, tp.TEMPLATE_HEADER.FILETYPE), inboundEncodedKeys);
      if (screen === 'outbound') {
        // Special handling for GraphQL: do not generate a full rc-tree, but keep a minimal record node in translatedData
        if (tp?.TEMPLATE_HEADER?.FILETYPE === 'GraphQL') {
          const record = tp.BODY?.RECORD?.[0] || tp.BODY || {};
          const root = {
            ...record,
            key: tab === 'BODY' ? 'ob-0' : tab === 'FILE_HEADER' ? 'obh-0' : 'obt-0',
            title: i18n.t('LBL_RECORD'),
            children: [],
            nodeId: genKey('root'),
            alternateNodeId: genKey('root'),
            flatIndex: 0,
            ICON_DISPLAY: 'RECORD',
            GRAPHQL_RECORD: 'TRUE',
            RECORD_DETAILS: record.RECORD_DETAILS || [],
          };
          return deepSearchAndDecode([root], outboundEncodedKeys);
        }
        return deepSearchAndDecode(outboundTemplateToRcTree(tp[tab], tp.TEMPLATE_HEADER, tab), outboundEncodedKeys);
      }
      //if (screen === 'workflow') return workflow translation here
    }
    rt.header.rawData = tp[typeHeader[screen]] ? payload : [];
    rt.header.translatedData = rt.header.initialData = tp[typeHeader[screen]]
      ? getTranslation(typeHeader[screen])
      : []; 

    rt.body.rawData = tp.BODY ? payload : [];
    rt.body.translatedData = rt.body.initialData = tp.BODY
      ? getTranslation("BODY")
      : [];

    rt.trailer.rawData = tp[typeTrailer[screen]] ? payload : [];
    rt.trailer.translatedData = rt.trailer.initialData = tp[typeTrailer[screen]]
      ? getTranslation(typeTrailer[screen])
      : [];

    state[screen].rightTree.fetchError = false;
  } else if (isValidTemplate) {
    if (screen === 'workflow') {
      let wfTemplateHeader = cloneDeep(payload.WORKFLOW_DEFINITION);
      state.workflow.rightTree.tree.body.translatedData = normalizeWorkflowVariableWfItemIdsDeep(
        deepSearchAndDecode(workflowTemplateToRcTree(payload), workflowEncodedKeys, workflowEncodedKeysItemList)
      );
      //WF_ITEM is not needed on frontend after translatedData is set.
      //This prevents keeping an old "initial" state of the template in the templateHeader, which could be tricky when saving from rcTree to Wf template.
      // if (wfTemplateHeader?.WORKFLOW?.WF_ITEM) delete wfTemplateHeader.WORKFLOW.WF_ITEM;
      state.workflow.rightTree.tree.templateHeader = deepSearchAndDecode(wfTemplateHeader, workflowEncodedKeys, workflowEncodedKeysItemList);
    } else {
      // Sometimes a get_inbound_template request will return nothing? 
      // This is a temporary way to atleast give the user feedback when it happens.
      state[screen].rightTree.fetchError = true;
    }
  }
  state[screen].rightTree.loading = false
}

export function rcTreeToInboundTemplate(tree) {
  let output = { 
    TEMPLATE: {
      TEMPLATE_HEADER: { ...tree.templateHeader}
    } 
  };
  let filetype = tree.templateHeader.FILETYPE;

  function mapRecordDetails(arr) {
    return arr.map(e => {
      let key = ["FIELD_GROUP", "REPEATING_FIELD_GROUP"].includes(e.ICON_DISPLAY) ? "FIELD_GROUP" : "FIELD";
      if (e.ICON_DISPLAY === "COMMENT") key = "COMMENT_ITEM";
      let obj = {[key]: { ...e }}

      if (e.children?.length) obj[key].RECORD_DETAILS = mapRecordDetails(e.children);
      // if (e.MAPNAME === e.DESCRIPTION) delete obj[key]['DESCRIPTION'];
      if (!["XML", "JSON"].includes(filetype) && obj[key].hasOwnProperty("GUI_XML_SORT_ORDER")) delete obj[key]["GUI_XML_SORT_ORDER"];
      if (obj[key]["GUI_XML_SORT_ORDER"] && typeof obj[key]["GUI_XML_SORT_ORDER"] !== "string") obj[key]["GUI_XML_SORT_ORDER"] = obj[key]["GUI_XML_SORT_ORDER"].toString();
      if (e.ICON_DISPLAY === "COMMENT") delete obj[key]["DESCRIPTION"];
      ['key', 'title', 'type', 'children', 'fieldNumber', 'isLeaf', 'treeType', 'nodeId', 'alternateNodeId', 'parent','PARENT','RECORD_TYPE', 'flatIndex', 'SCHEMA'].forEach(el => {delete obj[key][el]});
      return obj;
    })
  }

  function mapRecordArr(arr, tab) {
    // Treat body different because special display root "Body" in tree that 
    // is not present in the actual data.
    if (tab === "body") {
      arr = arr[0].children;
    }
    return arr.map((e) => {
      let records = [];
      let recordDetails = [];
      let obj = {...e};
      
      if (e.children) {
        e.children.forEach((child) => {
          if (['COMMENT', 'FIELD', 'FIELD_GROUP', 'ATTRIBUTE', 'DATABASE_FIELD', 'FIELD_LOOP', 'REPEATING_FIELD_GROUP'].includes(child.ICON_DISPLAY)
              || (['ELEMENT', 'REPEATING_ELEMENT'].includes(child.ICON_DISPLAY) && (!child?.children?.length && !child.TYPE))) recordDetails.push(child);
          else if (['RECORD', 'QUERY'].includes(child.ICON_DISPLAY) || (['ELEMENT', 'REPEATING_ELEMENT'].includes(child.ICON_DISPLAY) && (child?.children?.length || child.TYPE)) ) records.push(child);
        });

        if (recordDetails.length) obj.RECORD_DETAILS = mapRecordDetails(recordDetails);
        if (records.length) {obj.RECORD = mapRecordArr(records);}
      }
      
      if (!["XML", "JSON"].includes(filetype)) delete obj["GUI_XML_SORT_ORDER"];
      if (obj["GUI_XML_SORT_ORDER"] && typeof obj["GUI_XML_SORT_ORDER"] !== "string") obj["GUI_XML_SORT_ORDER"] = obj["GUI_XML_SORT_ORDER"].toString();
      ['key', 'title', 'type', 'children', 'fieldNumber', 'isLeaf', 'treeType', 'nodeId','alternateNodeId','parent','PARENT','RECORD_TYPE', 'flatIndex'].forEach(e => {delete obj[e]});
      if(obj?.CONDITION) {
        obj.CONDITION = cleanMappingCondition(obj?.CONDITION)
      }
      return obj;
    });
  }

  for (let key in tree) {
    if (["header", "body", "trailer"].includes(key) && tree[key].translatedData.length) {
      let tData = tree[key].translatedData;
      // add case for empty body templates. return empty object like received.
      if (tData[0].treeType && tData[0].treeType === 'BODY') {
        if (!tData[0].children.length) {
          output.TEMPLATE[key.toUpperCase()] = {
            RECORD: []
          };
        } else {
          output.TEMPLATE[key.toUpperCase()] = {
            RECORD: mapRecordArr(tData, key),
          };
        }
      } else {
        if (tData.length) {
          output.TEMPLATE[key.toUpperCase()] = {
            RECORD: mapRecordArr(tData, key),
          };
        } else {
          output.TEMPLATE[key.toUpperCase()] = {
            RECORD: []
          };
        }
      }
    }


    if (key === "condition") {
      if (tree.condition.length) {
        // WIP - here figure how to save conditions
        output.TEMPLATE.CONDITION = [];
      }
    }
  }

  let tHeader = output.TEMPLATE.TEMPLATE_HEADER;
  let header = output.TEMPLATE.HEADER ? output.TEMPLATE.HEADER : false;
  let trailer = output.TEMPLATE.TRAILER ? output.TEMPLATE.TRAILER : false;
  
  try {
    //if there is header root node, make sure HEADER_RECORD_TYPE = root node .TYPE
    //else delete HEADER_RECORD_TYPE prop from TEMPLATE_HEADER
    if (header && header.RECORD && header.RECORD[0]?.TYPE) tHeader.HEADER_RECORD_TYPE = header.RECORD[0].TYPE;
    else delete tHeader.HEADER_RECORD_TYPE; 

    //if there is trailer root node, make sure TRAILER_RECORD_TYPE = root node .TYPE
    //else delete TRAILER_RECORD_TYPE prop from TEMPLATE_HEADER
    if (trailer && trailer.RECORD && trailer.RECORD[0]?.TYPE) tHeader.TRAILER_RECORD_TYPE = trailer.RECORD[0].TYPE;
    else delete tHeader.TRAILER_RECORD_TYPE; 
  } catch (err) {
    //leaving this log in during development for potential err info
    console.log('ref err in trailer/header tprop assignment/delete: ', err);
  }
  
  return deepSearchAndEncode(stringifyJSONAttributes((output)), inboundEncodedKeys);
}

//convert outbound template from translatedData in redux to outbound tempalte JSON that backend uses.
export function rcTreeToOutboundTemplate(input) {
  //set initial structure that all outbound templates have
  let output = { 
    TEMPLATE: {
      TEMPLATE_HEADER: { ...input.templateHeader},
    } 
  };

  //wipe frontend-only props from given node also remove props whose value is undefined
  function wipeUnneededProps(node) {
    Object.keys(node).forEach(key => {
      if (key !== key.toUpperCase() || node[key] === undefined) delete node[key];
    });
  }

  //process a given array of nodes recursively.
  function processChildren(childrenArray) {
    let newArray = childrenArray.map((obj, i) => {
      let thisNode = {...obj};
      let childrenCopy = thisNode?.children?.length > 0 ? [...thisNode.children] : false;
      let keyName = thisNode.ICON_DISPLAY;

      //after node and its children are copied, remove fields that only frontend uses 
      wipeUnneededProps(thisNode);

      //correct discrepancies in prop's key name and the node's ICON_DISPLAY
      if (keyName === "CONDITION_IF") keyName = "IF";
      if (keyName === "CONDITION_ELSE") keyName = "ELSE";
      if (keyName === "RECORD_LOOP") keyName = "RECORD";
      if (["VARIABLE", "VARIABLE_COLLECTION"].includes(keyName)) keyName = "VARIABLE_LIST";
      if (keyName === "SEQUENCE") keyName = "CUSTOM_FIELD"
      if (keyName === "QUERY") keyName = thisNode.TYPE === "QUERY" ? "CUSTOM_FIELD" : "RECORD";
      if (keyName === "FIELD_GROUP" || thisNode["FIELD_GROUP"] || keyName === "REPEATING_FIELD_GROUP") keyName = "RECORD";
      if (["DATABASE_FIELD", "DATABASE_FIELD_VALID", "DATABASE_FIELD_KEY", "DATABASE_FIELD_VALID_KEY", "ATTRIBUTE", "FIELD_LOOP"].includes(keyName)) keyName = "FIELD";
      if (["ELEMENT", "REPEATING_ELEMENT"].includes(keyName)) {
        if (childrenCopy) keyName = "RECORD";
        else keyName = "FIELD";
      }

      thisNode = {[keyName]: {...thisNode}};
      let props = thisNode[keyName];

      //check if node has children. if true, process the children nodes
      function checkForChildren(propName) {
        if (childrenCopy) props[propName] = processChildren(childrenCopy);
      }

      function checkForXMLChildren() {
        if (childrenCopy) {
          let attrList = [];
          let recDetails = [];

          childrenCopy.forEach(o => {
            //rule to determine if attrList or recDetails is confusing. 
            //Based on my testing, it appears that fieldtype const goes into RECORD_DETAILS, and others go to ATTRIBUTE_LIST
            //TODO: need to research every edge case and/or get explaination from expert like Scott and Terry
            if (o?.FIELDTYPE === "CONSTANT" && o?.XML_ATTRIBUTE !== "TRUE") recDetails.push(o);
            else attrList.push(o);
          });
          if (attrList.length > 0) props["ATTRIBUTE_LIST"] = processChildren(attrList);
          if (recDetails.length) props["RECORD_DETAILS"] = processChildren(recDetails);
        }
      }

      //if node is record type, use RECORD_DETAILS prop to hold children array
      if (["RECORD"].includes(keyName)) {
        if (input?.templateHeader?.FILETYPE === "XML" && ["REPEATING_ELEMENT", "ELEMENT"].includes(props.ICON_DISPLAY)) checkForXMLChildren();
        else checkForChildren("RECORD_DETAILS");
      } 

      //special structuring for IF/ELSE nodes
      if (keyName === "IF") {
        let elseNode = childrenArray?.[i + 1]?.ICON_DISPLAY ==="CONDITION_ELSE" ? {...childrenArray[i + 1]} : false;
        thisNode = {
          IF: {...thisNode["IF"], "IF_FIELDLIST": childrenCopy ? processChildren(childrenCopy) : []}
        }
        if (elseNode) {
          let elseChildren = childrenArray?.[i + 1]?.children?.length > 0 ? childrenArray?.[i + 1]?.children : false;
          // Only add ELSE node if it has children
          if (elseChildren && elseChildren.length > 0) {
            thisNode.ELSE = {...elseNode, "ELSE_FIELDLIST": processChildren(elseChildren)};
            wipeUnneededProps(thisNode.ELSE);
          }
        } 
      }

      //fields dont have ICON_DISPLAY prop. XML field elements and attributes also dont.
      if (keyName === "FIELD" &&  ["FIELD", "ELEMENT", "ATTRIBUTE"].includes(thisNode[keyName].ICON_DISPLAY)) delete thisNode[keyName].ICON_DISPLAY;

      //return formatted node
      if (keyName !== "ELSE") return thisNode;
      else return undefined;
    }).filter(o => o !== undefined); // filters out duplicate else nodes that were set to  undefined.
    return newArray;
  }


  //process parent node
  function processParent(recordNode, root) {
    let thisNode = {...recordNode};
    let childrenCopy = thisNode?.children?.length > 0 ? [...thisNode.children] : false;

    wipeUnneededProps(thisNode);

    if (["RECORD", "RECORD_LOOP", "JSON_TOP_RECORD", "QUERY", "ELEMENT", "REPEATING_ELEMENT", "RECORD_TOP"].includes(thisNode.ICON_DISPLAY)) {
      if (root) {
        if (childrenCopy) {
          thisNode.RECORD_DETAILS = processChildren(childrenCopy);
        } else {
    
        }
      }
    } else {
      //TODO: only tested on CSV for now. need to add more root node types.
    }
    return thisNode;
  }

  if (input?.header?.translatedData?.[0]) {
    output.TEMPLATE.FILE_HEADER = processParent(input.header.translatedData[0], 'header')
  }
  if (input?.body?.translatedData?.[0]) {
    output.TEMPLATE.BODY = processParent(input.body.translatedData[0], 'body')
  }
  if (input?.trailer?.translatedData?.[0]) {
    output.TEMPLATE.FILE_TRAILER = processParent(input.trailer.translatedData[0], 'trailer')
  }

  return deepSearchAndEncode(stringifyJSONAttributes((output)), outboundEncodedKeys);
}

//convert Outbound XML templates from rcTree format to JSON format. 
// Separated from `rcTreeToOutboundTemplate` function due to complexity.
export function rcTreeToOutboundXML(input, initial) {

  //base object
  let output = {
    TEMPLATE: {}
  };

  //wipe frontend-only props from given node
  function wipeUnneededProps(node) {
    Object.keys(node).forEach(key => {
      if (key !== key.toUpperCase()) delete node[key];
    });
  }


  // --- XML BODY FUNCTIONS ---

  //identify different children types and route to propper processing functions
  //accept array of children, return object {RECORD_DETAILS: [...], ATTRIBUTE_LIST: [...]}
  function routeChildrenB(childrenArr, type) {
    // TODO: need to refactor this function to be more dynamic when I have more time...
    let outputObj = {};
    let attributeList = [];
    let recordDetails = [];

    //it appears that if there is an attribute in the array, all items before and including the last attribute are placed in the "ATTRIBUTE_LIST" array.
    //everything after that is placed in the "RECORD_DETAILS" array.
    let lastAttributeIndex = childrenArr.findLastIndex((o) => o.XML_ATTRIBUTE === "TRUE");
    
  
    childrenArr.forEach((value, i) => {
      
      //assign proper keys to oject values based on node type
      if (value.XML_ATTRIBUTE === "TRUE" ) {
        //ATTRIBUTE nodes use FIELD for key

        //remove ICON_DISPLAY prop from attributes
        let copyVal = cloneDeep(value);
        delete copyVal.ICON_DISPLAY;

        if (lastAttributeIndex >= i) {
          attributeList.push({"FIELD": copyVal});
        } else {
          recordDetails.push({"FIELD": copyVal});
        }

        // if (type === "element") attributeList.push({"FIELD": copyVal});
        // else recordDetails.push({"FIELD": copyVal});
      } else if (["VARIABLE", "VARIABLE_COLLECTION"].includes(value.TYPE)) {
        //VARIABLE AND VARIABLE_COLLECTION nodes use VARIABLE_LIST for key

        if (lastAttributeIndex >= i) {
          attributeList.push({"VARIABLE_LIST": cloneDeep(value)});
        } else {
          recordDetails.push({"VARIABLE_LIST": cloneDeep(value)});
        }

        // if (type === "element") attributeList.push({"VARIABLE_LIST": cloneDeep(value)});
        // else recordDetails.push({"VARIABLE_LIST": cloneDeep(value)});
      } else if (["SEQUENCE", "QUERY"].includes(value.TYPE)) {
        //SEQUENCE and QUERY nodes use CUSTOM_FIELD for key
        if (lastAttributeIndex >= i) {
          attributeList.push({"CUSTOM_FIELD": cloneDeep(value)});
        } else {
          recordDetails.push({"CUSTOM_FIELD": cloneDeep(value)});
        }

        // if (type === "element") attributeList.push({"CUSTOM_FIELD": cloneDeep(value)});
        // else recordDetails.push({"CUSTOM_FIELD": cloneDeep(value)});
      } else if (["PLUGIN", "ACTION_ITEM", "TRACKING_REPORT_ITEM"].includes(value.ICON_DISPLAY)) {
        //PLUGIN, ACTION_ITEM, and TRACKING_REPORT_ITEM nodes use same key as their ICON_DISPLAY

        if (lastAttributeIndex >= i) {
          attributeList.push({[value.ICON_DISPLAY]: cloneDeep(value)});
        } else {
          recordDetails.push({[value.ICON_DISPLAY]: cloneDeep(value)});
        }

        // if (type === "element") attributeList.push({[value.ICON_DISPLAY]: cloneDeep(value)});
        // else recordDetails.push({[value.ICON_DISPLAY]: cloneDeep(value)});
      } else if (value.ICON_DISPLAY === "CONDITION_IF") {
        //combine IF and ELSE nodes into one object
        //TODO: need to process the children nodes too...
        if (childrenArr[i + 1]?.ICON_DISPLAY === "CONDITION_ELSE") {
          if (lastAttributeIndex >= i) {
            attributeList.push(
              processIfElseB({
                "ELSE": cloneDeep(childrenArr[i + 1]),
                "IF": cloneDeep(value)
              })
            );
          } else {
            recordDetails.push(
              processIfElseB({
                "ELSE": cloneDeep(childrenArr[i + 1]),
                "IF": cloneDeep(value)
              })
            );
          }

          // if (type === "element") {
          //   attributeList.push(
          //     processIfElseB({
          //       "ELSE": cloneDeep(childrenArr[i + 1]),
          //       "IF": cloneDeep(value)
          //     })
          //   );
          // } else {
          //   recordDetails.push(
          //     processIfElseB({
          //       "ELSE": cloneDeep(childrenArr[i + 1]),
          //       "IF": cloneDeep(value)
          //     })
          //   );
          // }
        } else {
          if (lastAttributeIndex >= i) {
            attributeList.push(processIfElseB({"IF": cloneDeep(value)}));
          } else {
            recordDetails.push(processIfElseB({"IF": cloneDeep(value)}));
          }

          // if (type === "element") attributeList.push(processIfElseB({"IF": cloneDeep(value)}));
          // else recordDetails.push(processIfElseB({"IF": cloneDeep(value)}));
        }
      } else if (value.ELEMENT_NAME) {
        //ELEMENT node use key "RECORD" if they have children, and "FIELD" if they don't
        let copyVal = cloneDeep(value);

        // OLD CODE - COMMENTED OUT (caused nested FIELD issue - value.FIELD check wrapped elements in RECORD)
        // if (value.children?.length > 0 || value.FIELD) {
        //   if (lastAttributeIndex >= i) {
        //     attributeList.push({"RECORD": processElementB(copyVal)});
        //   } else {
        //     recordDetails.push({"RECORD": processElementB(copyVal)});
        //   }
        // } else {  
        //   delete copyVal.ICON_DISPLAY;
        //   if (lastAttributeIndex >= i) {
        //     attributeList.push({"FIELD": processElementB(copyVal)});
        //   } else {
        //     recordDetails.push({"FIELD": processElementB(copyVal)});
        //   }
        // }

        // NEW CODE - FIX: Only use RECORD if element has children, flatten FIELD properties for elements without children
        if (value.children?.length > 0) {
          // Element WITH children → wrap in RECORD and process children
          if (lastAttributeIndex >= i) {
            attributeList.push({"RECORD": processElementB(copyVal)});
          } else {
            recordDetails.push({"RECORD": processElementB(copyVal)});
          }
        } else {
          // Element WITHOUT children → flatten FIELD properties and wrap in FIELD (matches SWT output)
          let flatElement = {...copyVal};
          
          // Merge nested FIELD properties to top level (for inbound data mapping)
          if (copyVal.FIELD) {
            Object.assign(flatElement, copyVal.FIELD);
            delete flatElement.FIELD;
          }
          
          // FIELD elements don't have ICON_DISPLAY prop in backend
          delete flatElement.ICON_DISPLAY;
          
          if (lastAttributeIndex >= i) {
            attributeList.push({"FIELD": flatElement});
          } else {
            recordDetails.push({"FIELD": flatElement});
          }
        }
      } else if (value.XML_RECORD === "TRUE") {
        let copyVal = cloneDeep(value);
        copyVal.ICON_DISPLAY = "ELEMENT";

        if (lastAttributeIndex >= i) {
          attributeList.push({"RECORD": processRecordB(copyVal)});
        } else {
          recordDetails.push({"RECORD": processRecordB(copyVal)});
        }
        // recordDetails.push({"RECORD": processRecordB(copyVal)});
      }
    })

    if (type === "element") {
      if (attributeList.length > 0) outputObj.ATTRIBUTE_LIST = attributeList;
      if (recordDetails.length > 0) outputObj.RECORD_DETAILS = recordDetails;
    } else {
      return recordDetails;
    }

    return outputObj;
  }

  //process if/else nodes in xml body
  function processIfElseB(ifElseObj) {
    let outputObj = {};

    if (ifElseObj.IF) {
      outputObj.IF = {};
      //loop IF node's key/values, and set to new object.
      Object.entries(ifElseObj.IF).forEach((keyVal) => {
        const key = keyVal[0];
        const val = keyVal[1];

        //permit only uppercase keys
        if (isUpperCase(key)) {
          if (key === "FIELD") {
            //process the FIELD's key/vals before setting to outputObj.
            outputObj.IF["FIELD"] = processFieldB(val)
          } else {
            //set key/val normally.
            outputObj.IF[key] = val;
          }
        } else if (key === "children") {
          //children need to be separated by ATTRIBUTE_LIST or RECORD_DETAILS depending on child node type
          outputObj.IF.IF_FIELDLIST = routeChildrenB(val, 'if');
        }
      })
    }
    
    if (ifElseObj.ELSE) {
      outputObj.ELSE = {};
      //loop ELSE node's key/values, and set to new object.
      Object.entries(ifElseObj.ELSE).forEach((keyVal) => {
        const key = keyVal[0];
        const val = keyVal[1];

        //permit only uppercase keys
        if (isUpperCase(key)) {
          if (key === "FIELD") {
            //process the FIELD's key/vals before setting to outputObj.
            outputObj.ELSE["FIELD"] = processFieldB(val)
          } else {
            //set key/val normally.
            outputObj.ELSE[key] = val;
          }
        } else if (key === "children") {
          //children need to be separated by ATTRIBUTE_LIST or RECORD_DETAILS depending on child node type
          outputObj.ELSE.ELSE_FIELDLIST = routeChildrenB(val, 'else');
        }
      })
    }
    
    return outputObj;
  }

  //process field in xml body
  function processFieldB(fieldObj) {
    let outputObj = {};

    //loop element's key/values, and set to new object.
    Object.entries(fieldObj).forEach((keyVal) => {
      if (isUpperCase(keyVal[0])) outputObj[keyVal[0]] = keyVal[1]; 
    });

    return outputObj;
  }

  //process element in xml body
  function processElementB(elementObj) {
    let outputObj = {};
    let fieldPropertiesCollector = {}; // Collect properties that should go in FIELD
    
    // Properties that stay at RECORD level (not moved to FIELD)
    const recordLevelProps = [
      'ELEMENT_NAME', 'ICON_DISPLAY', 'LOOP', 'LOOP_SOURCE_NAME', 'TYPE', 
      'SOURCE_TYPE', 'EMPTY_ACTION', 'FILE_TYPE', 'XML_RECORD', 'PARENT'
    ];
    
    // Properties that should be at BOTH RECORD and FIELD level
    const duplicateProps = ['PARENT'];
    
    // Check if this element has children (will be wrapped in RECORD)
    const hasChildren = elementObj.children && elementObj.children.length > 0;
    
    //loop element's key/values, and set to new object.
    Object.entries(elementObj).forEach((keyVal) => {
      const key = keyVal[0];
      const val = keyVal[1];

      //permit only uppercase keys
      if (isUpperCase(key)) {
        if (key === "FIELD") {
          // Store nested FIELD properties - will merge later
          fieldPropertiesCollector = { ...fieldPropertiesCollector, ...processFieldB(val) };
        } else if (hasChildren && !recordLevelProps.includes(key)) {
          // For elements with children: collect field formatting properties
          fieldPropertiesCollector[key] = val;
        } else if (hasChildren && duplicateProps.includes(key)) {
          // Properties that should be at BOTH levels
          outputObj[key] = val;
          fieldPropertiesCollector[key] = val;
        } else {
          //set key/val normally at element level.
          outputObj[key] = val;
        }
      } else if (key === "children" && val.length > 0) {
        //children need to be separated by ATTRIBUTE_LIST or RECORD_DETAILS depending on child node type
        const childrenObj = routeChildrenB(val, "element");
        if (childrenObj.ATTRIBUTE_LIST) outputObj.ATTRIBUTE_LIST = childrenObj.ATTRIBUTE_LIST;
        if (childrenObj.RECORD_DETAILS) outputObj.RECORD_DETAILS = childrenObj.RECORD_DETAILS;
      }
    })
    
    // If we collected any FIELD properties, add them to output
    if (Object.keys(fieldPropertiesCollector).length > 0) {
      outputObj["FIELD"] = fieldPropertiesCollector;
    }
    
    return outputObj;
  }

  //process record in xml body
  function processRecordB(recordObj) {
    let outputObj = {};
    //loop element's key/values, and set to new object.
    Object.entries(recordObj).forEach((keyVal) => {
      const key = keyVal[0];
      const val = keyVal[1];

      //permit only uppercase keys
      if (isUpperCase(key)) {
        if (key === "FIELD") {
          //process the FIELD's key/vals before setting to outputObj.
          outputObj["FIELD"] = processFieldB(val)
        } else {
          //set key/val normally.
          outputObj[key] = val;
        }
      } else if (key === "children" && val.length > 0) {
        //children need to be separated by ATTRIBUTE_LIST or RECORD_DETAILS depending on child node type
        // outputObj.RECORD_DETAILS = routeChildrenB(val, "record");
        let childrenObj = routeChildrenB(val, "record");

        if (childrenObj.ATTRIBUTE_LIST) outputObj.ATTRIBUTE_LIST = childrenObj.ATTRIBUTE_LIST;
        if (childrenObj.RECORD_DETAILS) outputObj.RECORD_DETAILS = childrenObj.RECORD_DETAILS;
        if (!childrenObj.ATTRIBUTE_LIST && !childrenObj.RECORD_DETAILS && outputObj.length > 0) outputObj.RECORD_DETAILS = childrenObj;
      }
    })
    
    return outputObj;
  }


  // --- XML HEADER/TRAILER FUNCTIONS ---

  //process a given array of nodes recursively.
  function processChildren(childrenArray) {
    let newArray = childrenArray.map((obj, i) => {
      let thisNode = {...obj};
      let childrenCopy = thisNode?.children?.length > 0 ? [...thisNode.children] : false;
      let keyName = thisNode.ICON_DISPLAY;

      //after node and its children are copied, remove fields that only frontend uses 
      wipeUnneededProps(thisNode);

      //correct discrepancies in prop's key name and the node's ICON_DISPLAY
      if (keyName === "CONDITION_IF") keyName = "IF";
      if (keyName === "CONDITION_ELSE") keyName = "ELSE";
      if (keyName === "RECORD_LOOP") keyName = "RECORD";
      if (["VARIABLE", "VARIABLE_COLLECTION"].includes(keyName)) keyName = "VARIABLE_LIST";
      if (keyName === "SEQUENCE") keyName = "CUSTOM_FIELD"
      if (keyName === "QUERY") keyName = thisNode.TYPE === "QUERY" ? "CUSTOM_FIELD" : "RECORD";
      if (keyName === "FIELD_GROUP" || thisNode["FIELD_GROUP"]) keyName = "RECORD";
      if (["DATABASE_FIELD", "DATABASE_FIELD_VALID", "DATABASE_FIELD_KEY", "DATABASE_FIELD_VALID_KEY", "ATTRIBUTE"].includes(keyName)) keyName = "FIELD";
      if (["ELEMENT", "REPEATING_ELEMENT"].includes(keyName)) {
        keyName = "RECORD";
      }

      thisNode = {[keyName]: {...thisNode}};
      let props = thisNode[keyName];

      //check if node has children. if true, process the children nodes
      function checkForChildren(propName) {
        if (childrenCopy) props[propName] = processChildren(childrenCopy);
      }

      //if node is record type, use RECORD_DETAILS prop to hold children array
      if (keyName === "RECORD") {
        checkForChildren("RECORD_DETAILS");
      } 

      //special structuring for IF/ELSE nodes
      if (keyName === "IF") {
        let elseNode = childrenArray?.[i + 1]?.ICON_DISPLAY ==="CONDITION_ELSE" ? {...childrenArray[i + 1]} : false;
        thisNode = {
          IF: {...thisNode["IF"], "IF_FIELDLIST": childrenCopy ? processChildren(childrenCopy) : []}
        }
        if (elseNode) {
          let elseChildren = childrenArray?.[i + 1]?.children?.length > 0 ? childrenArray?.[i + 1]?.children : false;
          thisNode.ELSE = {...elseNode, "ELSE_FIELDLIST": elseChildren ? processChildren(elseChildren) : []}
          wipeUnneededProps(thisNode.ELSE);
        } 
      }

      //fields dont have ICON_DISPLAY prop. XML field elements and attributes also dont.
      if (keyName === "FIELD" &&  ["FIELD", "ELEMENT", "ATTRIBUTE"].includes(thisNode[keyName].ICON_DISPLAY)) delete thisNode[keyName].ICON_DISPLAY;

      //return formatted node
      if (keyName !== "ELSE") return thisNode;
      else return undefined;
    }).filter(o => o !== undefined); // filters out duplicate else nodes that were set to  undefined.
    return newArray;
  }

  //process parent node
  function processParent(recordNode, root) {
    let thisNode = {...recordNode};
    let childrenCopy = thisNode?.children?.length > 0 ? [...thisNode.children] : false;

    wipeUnneededProps(thisNode);

    if (["RECORD", "RECORD_LOOP", "JSON_TOP_RECORD", "QUERY", "ELEMENT", "REPEATING_ELEMENT"].includes(thisNode.ICON_DISPLAY)) {
      if (root) {
        if (childrenCopy) {
          thisNode.RECORD_DETAILS = processChildren(childrenCopy);
        } else {
          //
        }
      }
    } else {
      //
    }
    return thisNode;
  }

  //set FILE_HEADER
  if (input?.header?.translatedData?.[0]) {
    output.TEMPLATE.FILE_HEADER = processParent(input.header.translatedData[0], 'header')
  } else {
    // Check if header was explicitly deleted (empty array) vs never existed
    const headerWasDeleted = Array.isArray(input?.header?.translatedData) && input.header.translatedData.length === 0;
    
    if (headerWasDeleted) {
      // When header is explicitly deleted, don't include FILE_HEADER at all
      // This ensures nothing is sent back to backend
    } else if (initial?.TEMPLATE?.FILE_HEADER) {
      //if header root is not found, but initial has it, use initial's FILE_HEADER to prevent a ghost diff
      output.TEMPLATE.FILE_HEADER = initial.TEMPLATE.FILE_HEADER;
    } else {
      // Note: Commenting out default creation to match Java backend behavior
      // Previously this created defaults, but Java backend doesn't create them
      //if header root never existed, this is the object that SWT gives FILE_HEADER by default
      // output.TEMPLATE.FILE_HEADER = {
      //   "EMPTY_ACTION": "EMPTY",
      //   "FILE_TYPE": "STATIC",
      //   "ICON_DISPLAY": "REPEATING_ELEMENT",
      //   "LOOP": "MULTI"
      // };
    }
  }

  //set BODY data
  if (input?.body?.translatedData?.[0]) {
    output.TEMPLATE.BODY = processElementB(input?.body?.translatedData?.[0])
  } else {
    //if body root is deleted, this is the object that SWT gives BODY by default
    output.TEMPLATE.BODY = {
      "EMPTY_ACTION": "EMPTY",
      "FIELD": {
        "FIELDTYPE": "CONSTANT",
        "LENGTH": "0"
      },
      "ICON_DISPLAY": "ELEMENT",
      "LOOP": "SINGLE",
    };
  }

  //set FILE_TRAILER
  if (input?.trailer?.translatedData?.[0]) {
    output.TEMPLATE.FILE_TRAILER = processParent(input.trailer.translatedData[0], 'trailer')
  } else {
    // Check if trailer was explicitly deleted (empty array) vs never existed
    const trailerWasDeleted = Array.isArray(input?.trailer?.translatedData) && input.trailer.translatedData.length === 0;
    
    if (trailerWasDeleted) {
      // When trailer is explicitly deleted, don't include FILE_TRAILER at all
      // This ensures nothing is sent back to backend
    } else if (initial?.TEMPLATE?.FILE_TRAILER) {
      //if trailer root is not found, but initial has it, use initial's FILE_TRAILER to prevent a ghost diff
      output.TEMPLATE.FILE_TRAILER = initial.TEMPLATE.FILE_TRAILER;
    } else {
      // Note: Commenting out default creation to match Java backend behavior
      // Previously this created defaults, but Java backend doesn't create them
      //if trailer root never existed, this is the object that SWT gives FILE_TRAILER by default
      // output.TEMPLATE.FILE_TRAILER = {
      //   "EMPTY_ACTION": "EMPTY",
      //   "FILE_TYPE": "STATIC",
      //   "ICON_DISPLAY": "REPEATING_ELEMENT",
      //   "LOOP": "MULTI"
      // };
    }
  }

  //set TEMPLATE_HEADER
  if (input?.templateHeader) {
    output.TEMPLATE.TEMPLATE_HEADER = {};
    Object.keys(input.templateHeader).forEach((value) => {
      if (isUpperCase(value)) output.TEMPLATE.TEMPLATE_HEADER[value] = input.templateHeader[value];
    });
  }

  return deepSearchAndEncode(removeLowercaseKeys(output), outboundEncodedKeys);
}

export function rcTreeToWorkflowTemplate(tree) {
  const templateHeader = tree?.templateHeader;
  const body = tree?.body?.translatedData;
  const POSITIONX = body?.[0]?.POSITIONX;
  const POSITIONY = body?.[0]?.POSITIONY;

  let output = {
    WORKFLOW_DEFINITION: {
        ...templateHeader,
        WORKFLOW: {
          ...templateHeader?.WORKFLOW,
          ...(POSITIONX !== undefined && POSITIONY !== undefined && {POSITIONX, POSITIONY})
        }
    }
  };

  //remove lowercase keys from rcTree and ICON_DISPLAY
  const removeUnneededProps = (obj) => {
    let objClone = cloneDeep(obj);
    Object.keys(objClone).forEach((key) => {
      //Some keys are not in uppercase (like, "useNTLMv1" and "NTLMv2" used in MSMQ adapter), so we need to keep them
      if (key !== key.toUpperCase() && !["useNTLMv1", "NTLMv2"].includes(key)) delete objClone[key];
      if (key === "ICON_DISPLAY") delete objClone[key];
    });

    return objClone;
  }
  
  //process children nodes recursively. passing parent node incase we need it in the future
  const processChildren = (childrenArray, parentNode) => {
    let newChildArr = [];

    //loop through each item to process it, remove unneeded props, and loop through children if they exist
    childrenArray.forEach((child, i) => {
      //skip ELSE nodes, as they are processed in the IF node from [i - 1]
      if (child.ICON_DISPLAY === "CONDITION_ELSE") return;

      let newChild = cloneDeep(child);

      if (child.children?.length > 0) newChild.WF_ITEM = processChildren(child.children, child); 
      if (child.ELSE_NAME) {
        const EPOSITIONX = childrenArray[i + 1]?.POSITIONX;
        const EPOSITIONY = childrenArray[i + 1]?.POSITIONY;
        
        if(EPOSITIONX !== undefined && EPOSITIONY !== undefined){
          newChild.EPOSITIONX = EPOSITIONX;
          newChild.EPOSITIONY = EPOSITIONY;
        }
      }
      if (child.ELSE_NAME && childrenArray[i + 1]?.children?.length > 0) newChild.ELSE_WF_ITEM = processChildren(childrenArray[i + 1].children, childrenArray[i + 1]);
      
      if(child.ICON_DISPLAY === "CALL_PLUGIN") {
        const { NAME, ID, COMMENTS, ACTION, CONCURRENT, ENABLED, LOG_WFITEM_DETAILS, ICON_DISPLAY, ...rest } = newChild;
        
        // Filter rest to only include valid PLUGIN properties (uppercase keys)
        // and exclude empty IN_ARG and OUT_ARG arrays
        const pluginProps = {};
        Object.keys(rest).forEach(key => {
          if (key === key.toUpperCase() && !["useNTLMv1", "NTLMv2"].includes(key)) {
            // Exclude empty IN_ARG and OUT_ARG arrays
            if ((key === 'IN_ARG' || key === 'OUT_ARG') && Array.isArray(rest[key]) && rest[key].length === 0) {
              return;
            }
            pluginProps[key] = rest[key];
          }
        });
        
        newChild = { 
        NAME,
        ...(ID ? { ID } : {}),
        ...(COMMENTS ? { COMMENTS } : {}),
        ACTION,
        CONCURRENT,
        ENABLED,
        LOG_WFITEM_DETAILS,
        ICON_DISPLAY,
        "PLUGIN": pluginProps 
        };
      }

      newChildArr.push(Object.keys(newChild)?.sort()?.reduce((acc, key) => {
        acc[key] = newChild[key];
        return acc;
      }, {}));
    });

    return newChildArr;
  }

  //start loop on children of root node.
  output.WORKFLOW_DEFINITION.WORKFLOW.WF_ITEM = body[0]?.children?.length > 0 ? processChildren(body[0]?.children, templateHeader?.WORKFLOW) : [];

  //Passing removeUnneededProps function to deepSearchAndEncode to remove unneeded props from the object because needed ICON_DISPLAY prop in Encode function
  return deepSearchAndEncode(stringifyJSONAttributes((removeUnneededProps(output))), workflowEncodedKeys, workflowEncodedKeysItemList, removeUnneededProps);
}

//remove lower case keys from object recursively
function removeLowercaseKeys(obj) {
  let copyObj = cloneDeep(obj);
  //process an object, search for objects
  function processObject(input) {
    if (Array.isArray(input)) {
      // If the input is an array, process each element
      return input.map(element => processObject(element));
    } else if (typeof input === 'object' && input !== null) {
      // If the input is an object, process each property
      for (let key in input) {
        if (!isUpperCase(key)) {
          delete input[key];
        } else {
          input[key] = processObject(input[key]);
        }
      }
    }
    return input;
  }

  return processObject(copyObj);
}

export function isUpperCase(string) {
  return string === string.toUpperCase();
}

export const addAdditionalTemplateHeaderDetails = (data = {}, details = {}, isOutbound = false) => {
  const tempData = cloneDeep(data);
  let templateHeader = {...(tempData?.TEMPLATE?.TEMPLATE_HEADER || {} )};
  const template = tempData?.TEMPLATE || {};
  if (template?.TEMPLATE_HEADER) delete template?.TEMPLATE_HEADER;
  if (templateHeader.ACCOUNT_NAME) delete templateHeader.ACCOUNT_NAME;
  if (templateHeader.WF_GROUP) delete templateHeader.WF_GROUP;
  if (templateHeader.TEMPLATE) delete templateHeader.TEMPLATE;
  return { 
      TEMPLATE: {
        TEMPLATE_HEADER : {
          ...(templateHeader || {}),
          ...(isOutbound
            ? { TEMPLATENAME: `${details?.WF_GROUP}/${details.TEMPLATE}` }
            : { TEMPLATE: `${details?.WF_GROUP}/${details.TEMPLATE}` })
        },
        ...template
      },
  };
}

// Function for clearing all redundant keys form Record Property Mapping Condition
const cleanMappingCondition = (value) => {
  const newObj = JSON.parse(JSON.stringify(value,2))
  const removeRedundantKeys = (value = [], OPERATOR) => {
    value.forEach( obj => {
      ["ICON_DISPLAY", 
        "LBL_START_VAL", 
        "LBL_END_VAL",
        "title", 
        "key", 
        "LBL_START_POSITION", 
        "LBL_NUM_OF_CHARS", 
        "LBL_START_OCCUR", 
        "LBL_END_OCCUR", 
        "LBL_TEST_DATA",
        "children", 
        "RECORDTYPE",
        "TYPE",
        "readOnly",
        "LBL_PARSE_FROM",
        "LBL_FROM",
        "PORTIONING_TYPE"
      ]
      .forEach( e => {
        if (obj?.hasOwnProperty(e)) {
          delete obj?.[e];
        }
      })
      if(obj?.hasOwnProperty('COMPARE_ACTION_TRIM') && obj.COMPARE_ACTION_TRIM === 'FALSE') delete obj.COMPARE_ACTION_TRIM
      if (obj?.COMPARE !== "IS_DATE" && (obj?.PATTERN || obj?.PATTERN === '')) {
        delete obj.PATTERN;
      }
      if(obj?.hasOwnProperty('FIELD_OUTLINE_LEVEL') &&  obj?.LEVEL !== 'FIELD') delete obj.FIELD_OUTLINE_LEVEL
      // ["FIELD_OUTLINE_LEVEL", "FIELD_NAME", "MAPNAME", "FIELDNUM"].forEach((e) => {
      //   if (obj?.LEVEL !== "FIELD" && obj?.hasOwnProperty(e)) delete obj?.[e];
      //   if (obj?.hasOwnProperty(e) && !obj?.[e]) delete obj?.[e];
      // } )
      // if (!obj?.CHILDOPERATOR && OPERATOR) obj["OPERATOR"] = OPERATOR;
      // if (obj?.CHILDOPERATOR && obj?.hasOwnProperty("VALUE")) delete obj?.VALUE;
      if (obj?.CONDITION?.length) removeRedundantKeys(obj?.CONDITION , obj?.CHILDOPERATOR)
    })
  }

  removeRedundantKeys(newObj)
  return newObj;
}
